import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  CircularProgress,
} from "@mui/material";
import { Mic, Stop, Refresh } from "@mui/icons-material";
import MainLayout from "../../layouts/main/main_layout";
import { useFeedback } from "../../providers/feedback/feeedback";
import { transcribeVoice, VoiceTranscriptionResponse } from "../../services/voice/voice_service";
import styled from "@emotion/styled";

const StyledCard = styled(Card)({
  maxWidth: 800,
  margin: "0 auto",
  padding: "24px",
});

const RecordButton = styled(Button)({
  minWidth: "200px",
  height: "60px",
  fontSize: "18px",
  borderRadius: "30px",
});

const FieldContainer = styled(Box)({
  marginBottom: "16px",
});

export default function VoiceRecording() {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [formData, setFormData] = useState<VoiceTranscriptionResponse>({
    language: "",
    name: "",
    gender: "",
    age: "",
    profession: "",
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const { showSnackbar } = useFeedback();

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Try to use the best available audio format
      let mimeType = "audio/webm;codecs=opus";
      const options = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ];
      
      // Find the first supported format
      for (const option of options) {
        if (MediaRecorder.isTypeSupported(option)) {
          mimeType = option;
          break;
        }
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType,
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(audioBlob);
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach((track) => track.stop());

        // Send audio to API
        await sendAudioToAPI(audioBlob, mimeType);
      };

      mediaRecorder.start();
      setIsRecording(true);
      showSnackbar("Recording started. Speak now...");
    } catch (error) {
      console.error("Error accessing microphone:", error);
      showSnackbar("Error accessing microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      showSnackbar("Recording stopped. Processing...");
    }
  };

  const getFileExtension = (mimeType: string): string => {
    if (mimeType.includes("webm")) return "webm";
    if (mimeType.includes("mp4")) return "m4a";
    if (mimeType.includes("ogg")) return "ogg";
    return "webm"; // default
  };

  const sendAudioToAPI = async (blob: Blob, mimeType: string) => {
    setIsProcessing(true);
    try {
      // Create a file with appropriate extension based on mime type
      const extension = getFileExtension(mimeType);
      const audioFile = new File([blob], `recording.${extension}`, { type: mimeType });
      
      const response = await transcribeVoice(audioFile);
      
      if (response.data) {
        setFormData({
          language: response.data.language || "",
          name: response.data.name || "",
          gender: response.data.gender || "",
          age: response.data.age || "",
          profession: response.data.profession || "",
        });
        showSnackbar("Audio processed successfully!");
      }
    } catch (error: any) {
      console.error("Error transcribing audio:", error);
      const errorMessage = error?.message || "Error processing audio. Please try again.";
      showSnackbar(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setFormData({
      language: "",
      name: "",
      gender: "",
      age: "",
      profession: "",
    });
    setAudioBlob(null);
    audioChunksRef.current = [];
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setIsProcessing(false);
  };

  return (
    <MainLayout>
      <Box sx={{ padding: "24px" }}>
        <StyledCard>
          <CardContent>
            <Typography variant="h4" gutterBottom align="center" sx={{ mb: 4 }}>
              Voice Recognition & Transcription
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
              {!isRecording ? (
                <RecordButton
                  variant="contained"
                  color="primary"
                  startIcon={<Mic />}
                  onClick={startRecording}
                  disabled={isProcessing}
                >
                  Start Recording
                </RecordButton>
              ) : (
                <RecordButton
                  variant="contained"
                  color="error"
                  startIcon={<Stop />}
                  onClick={stopRecording}
                >
                  Stop Recording
                </RecordButton>
              )}
            </Box>

            {isProcessing && (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  Processing audio...
                </Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleReset}
                disabled={isRecording || isProcessing}
              >
                Reset
              </Button>
            </Box>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <FieldContainer>
                  <TextField
                    fullWidth
                    label="Language"
                    value={formData.language}
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </FieldContainer>
              </Grid>

              <Grid item xs={12} md={6}>
                <FieldContainer>
                  <TextField
                    fullWidth
                    label="Name"
                    value={formData.name}
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </FieldContainer>
              </Grid>

              <Grid item xs={12} md={6}>
                <FieldContainer>
                  <TextField
                    fullWidth
                    label="Gender"
                    value={formData.gender}
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </FieldContainer>
              </Grid>

              <Grid item xs={12} md={6}>
                <FieldContainer>
                  <TextField
                    fullWidth
                    label="Age"
                    value={formData.age}
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </FieldContainer>
              </Grid>

              <Grid item xs={12}>
                <FieldContainer>
                  <TextField
                    fullWidth
                    label="Occupation"
                    value={formData.profession}
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </FieldContainer>
              </Grid>
            </Grid>
          </CardContent>
        </StyledCard>
      </Box>
    </MainLayout>
  );
}

